import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoadingFallback } from '@/components/layout/LoadingFallback';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RouteErrorPage } from '@/components/layout/RouteErrorPage';

/*
 * Reititysstrategia (Sessio 6a + 6b):
 * - /login, /register, /auth/callback, /verify-email ovat julkisia
 *   (ei AppLayoutia, ei ProtectedRoutea)
 * - / ja moduulireitit ovat ProtectedRoute:n takana, AppLayout-shellissa
 * - Kaikki moduulit lazy-loadattu jotta initial bundle pysyy pienenä
 * - errorElement (BUG-H1, 30.10.2026): juuritasolla koko sivun virheruutu, moduulireiteillä
 *   kuoren sisäinen -- React Routerin oma 'Unexpected Application Error!' ei näy käyttäjälle
 *
 * Tulevat lisaykset:
 * - Sessio 7: /forgot-password
 */

const HomePage = lazy(() =>
  import('@/features/home/HomePage').then((m) => ({ default: m.HomePage })),
);
const IdeoiPage = lazy(() =>
  import('@/features/ideoi/IdeoiPage').then((m) => ({ default: m.IdeoiPage })),
);
const PlanPage = lazy(() =>
  import('@/features/plans/PlanPage').then((m) => ({ default: m.PlanPage })),
);
const DocumentEditorPage = lazy(() =>
  import('@/features/documents/DocumentEditorPage').then((m) => ({ default: m.DocumentEditorPage })),
);
const SuunnitteleLayout = lazy(() =>
  import('@/features/suunnittele/SuunnitteleLayout').then((m) => ({ default: m.SuunnitteleLayout })),
);
const TaskBoardPage = lazy(() =>
  import('@/features/tasks/TaskBoardPage').then((m) => ({ default: m.TaskBoardPage })),
);
const SuunnittelePage = lazy(() =>
  import('@/features/suunnittele/SuunnittelePage').then((m) => ({
    default: m.SuunnittelePage,
  })),
);
const TuotaPage = lazy(() =>
  import('@/features/tuota/TuotaPage').then((m) => ({ default: m.TuotaPage })),
);
const PostEditorPage = lazy(() =>
  import('@/features/tuota/PostEditorPage').then((m) => ({ default: m.PostEditorPage })),
);
const JulkaisePage = lazy(() =>
  import('@/features/julkaise/JulkaisePage').then((m) => ({
    default: m.JulkaisePage,
  })),
);
const SeuraaPage = lazy(() =>
  import('@/features/seuraa/SeuraaPage').then((m) => ({ default: m.SeuraaPage })),
);
const LibraryPage = lazy(() =>
  import('@/features/library/LibraryPage').then((m) => ({
    default: m.LibraryPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({
    default: m.SettingsPage,
  })),
);
const BillingPage = lazy(() =>
  import('@/features/billing/BillingPage').then((m) => ({
    default: m.BillingPage,
  })),
);
const LoginPage = lazy(() =>
  import('@/features/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/features/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
const OAuthCallbackPage = lazy(() =>
  import('@/features/auth/OAuthCallbackPage').then((m) => ({ default: m.OAuthCallbackPage })),
);
const EmailVerifyPage = lazy(() =>
  import('@/features/auth/EmailVerifyPage').then((m) => ({ default: m.EmailVerifyPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const MfaChallengePage = lazy(() =>
  import('@/features/auth/MfaChallengePage').then((m) => ({ default: m.MfaChallengePage })),
);
const AcceptInvitePage = lazy(() =>
  import('@/features/auth/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })),
);
const OnboardingPage = lazy(() =>
  import('@/features/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const GuidePage = lazy(() =>
  import('@/features/guide/GuidePage').then((m) => ({ default: m.GuidePage })),
);
const FeedbackPage = lazy(() =>
  import('@/features/feedback/FeedbackPage').then((m) => ({ default: m.FeedbackPage })),
);
const ViestiLayout = lazy(() =>
  import('@/features/viesti/ViestiLayout').then((m) => ({ default: m.ViestiLayout })),
);
const YhteenvetoView = lazy(() =>
  import('@/features/viesti/YhteenvetoView').then((m) => ({ default: m.YhteenvetoView })),
);
const LahetaViestiView = lazy(() =>
  import('@/features/viesti/LahetaViestiView').then((m) => ({ default: m.LahetaViestiView })),
);
const ListatView = lazy(() =>
  import('@/features/viesti/ListatView').then((m) => ({ default: m.ListatView })),
);
const TilaajatView = lazy(() =>
  import('@/features/viesti/TilaajatView').then((m) => ({ default: m.TilaajatView })),
);
const SegmentitView = lazy(() =>
  import('@/features/viesti/SegmentitView').then((m) => ({ default: m.SegmentitView })),
);
const TemplatetView = lazy(() =>
  import('@/features/viesti/TemplatetView').then((m) => ({ default: m.TemplatetView })),
);
const KampanjatView = lazy(() =>
  import('@/features/viesti/KampanjatView').then((m) => ({ default: m.KampanjatView })),
);
const CampaignEditorPage = lazy(() =>
  import('@/features/viesti/CampaignEditorPage').then((m) => ({
    default: m.CampaignEditorPage,
  })),
);
const JoinListPage = lazy(() =>
  import('@/features/join/JoinListPage').then((m) => ({ default: m.JoinListPage })),
);

export const router = createBrowserRouter([
  {
    // Pathless-juurireitti: kuoren tai julkisen sivun virhe -> koko sivun virheruutu.
    errorElement: <RouteErrorPage fullPage />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/register',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RegisterPage />
          </Suspense>
        ),
      },
      {
        // LM-2: julkinen liittymissivu (jaettava linkki + QR, docs/design/list-migration.md)
        path: '/liity/:slug',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <JoinListPage />
          </Suspense>
        ),
      },
      {
        path: '/auth/callback',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <OAuthCallbackPage />
          </Suspense>
        ),
      },
      {
        path: '/verify-email',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EmailVerifyPage />
          </Suspense>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: '/reset-password/:token',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: '/auth/mfa-challenge',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <MfaChallengePage />
          </Suspense>
        ),
      },
      {
        path: '/auth/accept',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AcceptInvitePage />
          </Suspense>
        ),
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/',
            element: <AppLayout />,
            children: [
              {
                // Pathless-reitti: virhe moduulissa näytetään kuoren sisällä (sivupalkki pysyy).
                errorElement: <RouteErrorPage />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <HomePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'ideoi',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <IdeoiPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Tyhjä dokumentti (VISIO-H1): IDEOI-listan dokumenttikortti ja kalenterin dokumenttichippi.
                    path: 'ideoi/dokumentit/:documentId',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <DocumentEditorPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Strategiasuunnitelma (planning-suite OSA C / C1): IDEOI → Strategiat → kortti.
                    path: 'ideoi/strategiat/:planId',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <PlanPage />
                      </Suspense>
                    ),
                  },
                  {
                    // B2a (planning-suite OSA B): välilehtikuori Kalenteri (index) | Tehtävät.
                    path: 'suunnittele',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <SuunnitteleLayout />
                      </Suspense>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <SuunnittelePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'tehtavat',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <TaskBoardPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'tuota',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <TuotaPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tuota/:postId',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <PostEditorPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'julkaise',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <JulkaisePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'seuraa',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <SeuraaPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Viesti (V6a1): nested routes -- välilehdet ovat reittejä, jotta
                    // deep-linkit (esim. /viesti/kampanjat/:id V6a2:ssa) toimivat.
                    path: 'viesti',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <ViestiLayout />
                      </Suspense>
                    ),
                    children: [
                      // V7a: etusivu = Yhteenveto (ajastetut + lähetetyt + luonnokset heti näkyviin)
                      { index: true, element: <Navigate to="yhteenveto" replace /> },
                      {
                        path: 'yhteenveto',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <YhteenvetoView />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'laheta',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <LahetaViestiView />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'listat',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <ListatView />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'tilaajat',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <TilaajatView />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'segmentit',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <SegmentitView />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'templatet',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <TemplatetView />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'kampanjat',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <KampanjatView />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'kampanjat/:campaignId',
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <CampaignEditorPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'library',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <LibraryPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'settings',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <SettingsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'billing',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <BillingPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'onboarding',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <OnboardingPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Palaute (20.8.2026): oma sivu sivupalkissa -- korvaa käyttäjävalikon modaalin.
                    path: 'palaute',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <FeedbackPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Käyttöopas (J16): /ohjeet ja /ohjeet/:slug renderöivät saman
                    // sivun -- slug valitsee luvun, ilman slugia näytetään ensimmäinen.
                    path: 'ohjeet',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <GuidePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'ohjeet/:slug',
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <GuidePage />
                      </Suspense>
                    ),
                  },
                  { path: '*', element: <Navigate to="/" replace /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
